import { useState } from "react";

interface FilterBarProps {
  type: string;
  sort: string;
  onTypeChange: (value: string) => void;
  onSortChange: (value: string) => void;
  resultCount: number;
}

const TYPE_OPTIONS = [
  { value: "all", label: "全部" },
  { value: "movie", label: "电影" },
  { value: "tv", label: "电视剧" },
];

const SORT_OPTIONS = [
  { value: "recommend", label: "默认推荐" },
  { value: "date", label: "按上映时间" },
  { value: "alpha", label: "按字母顺序" },
];

export default function FilterBar({
  type,
  sort,
  onTypeChange,
  onSortChange,
  resultCount,
}: FilterBarProps) {
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <>
      {/* 桌面端筛选栏 */}
      <div className="hidden md:flex items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
          {TYPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onTypeChange(option.value)}
              className={`px-5 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                type === option.value
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-foreground-500 whitespace-nowrap">
            共 {resultCount} 部作品
          </span>
          <div className="relative">
            <select
              value={sort}
              onChange={(e) => onSortChange(e.target.value)}
              className="appearance-none pl-4 pr-9 py-2 rounded-md text-sm bg-background-100 border border-background-200 text-foreground-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* 移动端筛选栏 */}
      <div className="md:hidden flex items-center justify-between gap-3 mb-6">
        <span className="text-sm text-foreground-500">共 {resultCount} 部作品</span>
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm bg-background-100 border border-background-200 text-foreground-700 cursor-pointer"
        >
          <i className="ri-filter-3-line" />
          筛选
        </button>
      </div>

      {sheetOpen && (
        <div className="md:hidden fixed inset-0 z-[60] flex items-end">
          <div
            className="absolute inset-0 bg-primary-950/50 backdrop-blur-sm"
            onClick={() => setSheetOpen(false)}
            aria-hidden="true"
          />
          <div className="relative w-full bg-background-50 rounded-t-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h4 className="font-heading font-semibold text-base text-foreground-950">筛选与排序</h4>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setSheetOpen(false)}
                className="flex items-center justify-center w-9 h-9 rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <p className="text-xs font-medium text-foreground-400 mb-3">作品类型</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {TYPE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onTypeChange(option.value)}
                  className={`px-4 py-2.5 rounded-md text-sm transition-colors cursor-pointer ${
                    type === option.value
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 border border-background-200"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <p className="text-xs font-medium text-foreground-400 mb-3">排序方式</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {SORT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onSortChange(option.value)}
                  className={`px-4 py-2.5 rounded-md text-sm transition-colors cursor-pointer ${
                    sort === option.value
                      ? "bg-accent-500 text-primary-950"
                      : "bg-background-100 text-foreground-600 border border-background-200"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setSheetOpen(false)}
              className="w-full py-3 rounded-md text-sm font-medium bg-primary-500 text-background-50 cursor-pointer"
            >
              查看结果（{resultCount}）
            </button>
          </div>
        </div>
      )}
    </>
  );
}